import { injectable } from 'inversify';
import { Collection, Db, getMongoDb } from "@swoft/persistence";
import { DesignImplementationFlow } from '../../domain/aggregates/DesignImplementationFlow';
import { FlowStatus, ArtifactStatus, ConformanceScore } from '../../domain/value-objects';
import { GTD_COLLECTIONS } from '../../../../config/collections';

/**
 * MongoDB Repository for DesignImplementationFlow Aggregate
 * 
 * Following Eric Evans repository pattern - provides collection-like
 * interface for accessing flows while hiding persistence details.
 * Based on GTD Project Repository pattern.
 */
@injectable()
export class DesignImplementationFlowRepository {
  private db: Db | null = null;
  private collection: Collection | null = null;

  constructor() {
    // Lazy initialization - don't connect until needed
  }

  private ensureConnection() {
    if (!this.db) {
      this.db = getMongoDb();
      this.collection = this.db.collection(GTD_COLLECTIONS.DESIGN_IMPLEMENTATION_FLOWS);
    }
    return this.collection!;
  }

  async save(flow: DesignImplementationFlow): Promise<void> {
    const collection = this.ensureConnection();
    const doc = {
      _id: flow.id,
      projectId: flow.projectId,
      createdBy: flow.createdBy,
      createdAt: flow.createdAt,
      metaModelBinding: flow.metaModelBinding,
      designSpecification: flow.designSpecification,
      status: flow.status.toString(),
      artifacts: flow.artifacts.map(artifact => ({
        id: artifact.id,
        name: artifact.name,
        type: artifact.type,
        version: artifact.version,
        location: artifact.location,
        status: artifact.status.toString(),
        conformanceScore: artifact.conformanceScore ? {
          metaModelScore: artifact.conformanceScore.metaModelScore,
          designCoverageScore: artifact.conformanceScore.designCoverageScore,
          overallScore: artifact.conformanceScore.overallScore,
          confidence: artifact.conformanceScore.confidence,
          calculatedAt: artifact.conformanceScore.calculatedAt
        } : null,
        healthStatus: artifact.healthStatus,
        createdBy: artifact.createdBy,
        createdAt: artifact.createdAt,
        lastModified: artifact.lastModified,
        tags: artifact.tags
      })),
      completedAt: flow.completedAt,
      updatedAt: new Date()
    };

    await collection.replaceOne(
      { _id: flow.id as any },
      doc,
      { upsert: true }
    );
  }

  async findById(id: string): Promise<DesignImplementationFlow | null> {
    const collection = this.ensureConnection();
    const doc = await collection.findOne({ _id: id as any });
    if (!doc) return null;

    return this.mapDocumentToDomain(doc);
  }

  async findByProjectId(projectId: string): Promise<DesignImplementationFlow[]> {
    const collection = this.ensureConnection();
    const docs = await collection
      .find({ projectId })
      .sort({ createdAt: -1 })
      .toArray();

    return docs
      .map(doc => this.safeMapDocumentToDomain(doc))
      .filter(flow => flow !== null) as DesignImplementationFlow[];
  }

  async findByStatus(status: string): Promise<DesignImplementationFlow[]> {
    const collection = this.ensureConnection();
    const docs = await collection
      .find({ status })
      .sort({ createdAt: -1 })
      .toArray();

    return docs
      .map(doc => this.safeMapDocumentToDomain(doc))
      .filter(flow => flow !== null) as DesignImplementationFlow[];
  }

  async findActiveFlows(): Promise<DesignImplementationFlow[]> {
    return this.findByStatus('active');
  }

  async findAll(): Promise<DesignImplementationFlow[]> {
    const collection = this.ensureConnection();
    const docs = await collection
      .find({})
      .sort({ createdAt: -1 })
      .toArray();

    return docs
      .map(doc => this.safeMapDocumentToDomain(doc))
      .filter(flow => flow !== null) as DesignImplementationFlow[];
  }

  async deleteById(id: string): Promise<void> {
    const collection = this.ensureConnection();
    await collection.deleteOne({ _id: id as any });
  }

  private mapDocumentToDomain(doc: any): DesignImplementationFlow {
    const flow = new DesignImplementationFlow(
      doc._id,
      doc.projectId,
      doc.createdBy,
      new Date(doc.createdAt),
      doc.metaModelBinding,
      doc.designSpecification
    );

    // Restore private state
    if (doc.status) {
      (flow as any)._status = FlowStatus.fromString(doc.status);
    }
    if (doc.completedAt) {
      (flow as any)._completedAt = new Date(doc.completedAt);
    }

    // Restore artifacts
    if (doc.artifacts && Array.isArray(doc.artifacts)) {
      const artifacts = doc.artifacts.map((artifactDoc: any) => ({
        id: artifactDoc.id,
        name: artifactDoc.name,
        type: artifactDoc.type,
        version: artifactDoc.version,
        location: artifactDoc.location,
        status: ArtifactStatus.fromString(artifactDoc.status),
        conformanceScore: artifactDoc.conformanceScore ? 
          new ConformanceScore(
            artifactDoc.conformanceScore.metaModelScore,
            artifactDoc.conformanceScore.designCoverageScore,
            artifactDoc.conformanceScore.confidence,
            new Date(artifactDoc.conformanceScore.calculatedAt)
          ) : undefined,
        healthStatus: {
          buildStatus: artifactDoc.healthStatus.buildStatus,
          testStatus: artifactDoc.healthStatus.testStatus,
          deploymentStatus: artifactDoc.healthStatus.deploymentStatus,
          lastHealthCheck: new Date(artifactDoc.healthStatus.lastHealthCheck)
        },
        createdBy: artifactDoc.createdBy,
        createdAt: new Date(artifactDoc.createdAt),
        lastModified: new Date(artifactDoc.lastModified),
        tags: artifactDoc.tags || []
      }));
      
      (flow as any)._artifacts = artifacts;
    }

    return flow;
  }

  private safeMapDocumentToDomain(doc: any): DesignImplementationFlow | null {
    try {
      // Validate required fields before attempting to create domain object
      if (!doc.projectId?.trim()) {
        console.warn(`Skipping flow document with missing projectId: ${doc._id}`);
        return null;
      }
      if (!doc.createdBy?.trim()) {
        console.warn(`Skipping flow document with missing createdBy: ${doc._id}`);
        return null;
      }

      return this.mapDocumentToDomain(doc);
    } catch (error) {
      console.warn(`Failed to map flow document to domain object: ${doc._id}`, error);
      return null;
    }
  }
}